import { Alert, Grid, Typography } from "@mui/material";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { WasteEpisodicEventContactMethod, WasteEpisodicEventType } from "generated-graphql/graphql";
import { Control, useController, useWatch } from "react-hook-form";

import { formatContainerDate } from "../Containers/containerDisplay";
import { contactDueDate, contactTimeliness, requiresEpaContactQuestion } from "./episodicDisplay";
import { EpisodicEventFormValues } from "./episodicEventFormTypes";
import { NoticeProofDocument, NoticeProofFormField } from "./noticeProof";

const EPA_CONTACT_RADIO_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const CONTACT_METHOD_SELECT_ITEMS = [
  { value: WasteEpisodicEventContactMethod.Phone, display: "Phone" },
  { value: WasteEpisodicEventContactMethod.Email, display: "Email" },
  { value: WasteEpisodicEventContactMethod.Fax, display: "Fax" },
];

/**
 * Non-blocking mirror of `contactTimeliness`, shown inline as soon as a
 * contact date is entered — a late or before-the-event date is still worth
 * recording, so this warns rather than blocks (the future-date check in
 * `EpisodicEventDialog`'s `validateEpaContact` is the one that blocks save).
 */
function epaContactTimelinessWarning(startDate: string, contactDate: string): string | null {
  const timeliness = contactTimeliness(startDate, contactDate);
  if (timeliness === "late") {
    return "This contact was outside the 72-hour window — record it anyway; a late record beats no record.";
  }
  if (timeliness === "beforeEvent") {
    return "This date is before the event's start date — double-check it.";
  }
  return null;
}

/**
 * The 72-hour EPA-contact capture for an unplanned event, once a start date
 * is set. Self-gates on `type`/`startDate` via `useWatch` so the parent
 * doesn't need to re-render on every keystroke to keep this in sync.
 *
 * Once `requiresEpaContactQuestion` goes true (today's UTC calendar date is
 * strictly after `contactDueDate`, i.e. day 4 or later — time zones never
 * factor in), the question becomes required — but only at the form layer:
 * `EpisodicEventDialog`'s `validateEpaContact` blocks Save on a null answer,
 * not on a "no" answer. Inside the window (through the whole of day 3) the
 * same fields render, optional, headed by the plain due date instead.
 */
export function EpaContactSection({
  control,
  existingProof = null,
}: {
  control: Control<EpisodicEventFormValues>;
  /** Proof already attached to the recorded EPA_CONTACT notice, if any. */
  existingProof?: NoticeProofDocument | null;
}) {
  const type = useWatch({ control, name: "type" });
  const startDate = useWatch({ control, name: "startDate" });
  const epaContacted = useWatch({ control, name: "epaContacted" });
  const epaContactDate = useWatch({ control, name: "epaContactDate" });
  // Contact proof is optional, so an already-attached document is removable
  // right here (unlike the external filing's, which has no "no proof" state).
  // Removal is a form value: the save maps it to a documentId: null detach.
  const { field: proofRemovedField } = useController({ control, name: "epaContactProofRemoved" });

  if (type !== WasteEpisodicEventType.Unplanned || !startDate) {
    return null;
  }

  const contactDateWarning =
    epaContactDate != null ? epaContactTimelinessWarning(startDate, epaContactDate) : null;

  const questionRequired = requiresEpaContactQuestion(type, startDate);

  return (
    <>
      {!questionRequired && (
        <Grid size={{ xs: 12 }}>
          <Typography variant="caption" color="text.secondary">
            {`Notify EPA by phone, email, or fax by ${formatContainerDate(contactDueDate(startDate))}.`}
          </Typography>
        </Grid>
      )}
      <Grid size={{ xs: 12 }}>
        <RadioGroupField
          name="epaContacted"
          label="Did you notify EPA? (EPA requires phone, email, or fax within 72 hours of an unplanned event)"
          control={control}
          radioOptions={EPA_CONTACT_RADIO_OPTIONS}
          required={questionRequired}
        />
      </Grid>
      {epaContacted === "yes" && (
        <>
          <Grid size={{ xs: 6 }}>
            <DateField name="epaContactDate" label="EPA contacted on" control={control} required />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormSelect
              name="epaContactMethod"
              label="Method"
              control={control}
              selectItems={CONTACT_METHOD_SELECT_ITEMS}
              rules={{ required: true }}
            />
          </Grid>
          {contactDateWarning && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning">{contactDateWarning}</Alert>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <NoticeProofFormField
              control={control}
              name="epaContactProofFile"
              existingProof={proofRemovedField.value ? null : existingProof}
              helperText="Attach proof of contact (optional) — e.g. a confirmation email."
              onRemoveExisting={() => proofRemovedField.onChange(true)}
            />
          </Grid>
        </>
      )}
      {epaContacted === "no" && (
        <Grid size={{ xs: 12 }}>
          <Alert severity="warning">
            {questionRequired
              ? "Contact EPA by phone, email, or fax as soon as possible — the 72-hour window has passed."
              : `Contact EPA by phone, email, or fax by ${formatContainerDate(contactDueDate(startDate))}.`}
          </Alert>
        </Grid>
      )}
    </>
  );
}
